import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import Seo, { composeTitle, truncateForMeta } from "@/components/Seo";
import { Container, SectionLabel } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";

const LEGAL_PATHS = {
  privacy: "/privacy",
  terms: "/terms",
  cookies: "/cookies",
  service_terms: "/service-terms",
  intellectual_property: "/intellectual-property",
};

function formatLastUpdated(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

/**
 * Shared renderer for CMS-driven legal pages. Content from /api/pages/{key}.
 * Shape: hero {eyebrow,title,effective_date,intro}, body {sections:[{heading,body}]}.
 */
export default function LegalPage({ pageKey, fallback }) {
  const { settings } = useSettings();
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    api.getPage(pageKey).then(setDoc).catch(() => {});
  }, [pageKey]);

  const c = doc?.content || {};
  const hero = c.hero || {};
  const body = c.body || {};
  const eyebrow = hero.eyebrow || fallback.eyebrow;
  const title = hero.title || fallback.title;
  const effective = hero.effective_date || fallback.effective_date;
  const intro = hero.intro || fallback.intro;
  const sections = Array.isArray(body.sections) && body.sections.length > 0 ? body.sections : fallback.sections;
  const lastUpdated = formatLastUpdated(doc?.updated_at);

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo
        title={composeTitle(title, settings.seo.default_title, settings.seo.title_suffix)}
        description={truncateForMeta(intro || settings.seo.default_description)}
        path={LEGAL_PATHS[pageKey]}
      />
      <TopBar />
      <Container className="pt-32 md:pt-40 pb-24" >
        <div className="max-w-[820px]">
          <SectionLabel>{eyebrow}</SectionLabel>
          <h1 className="font-serif font-light text-lux-ink tracking-tight leading-none mt-2 text-4xl md:text-6xl">{title}</h1>
          <p className="text-xs tracking-[0.2em] uppercase text-lux-muted mt-4">{effective}</p>
          {lastUpdated ? (
            <p className="text-[11px] tracking-[0.15em] uppercase text-lux-muted mt-1" data-testid={`${pageKey}-last-updated`}>
              Last Updated · {lastUpdated}
            </p>
          ) : null}
          <p className="text-base md:text-lg text-lux-sec leading-relaxed font-light mt-6 max-w-[720px]">{intro}</p>

          <div className="mt-12 flex flex-col gap-8 max-w-[760px]">
            {sections.map((s, i) => (
              <div key={s.heading || i} className="border-t border-lux-line pt-6 flex flex-col gap-2.5" data-testid={`${pageKey}-section-${i}`}>
                <span className="text-[10px] tracking-[0.25em] text-lux-accent">{`0${i + 1}`.slice(-2)}</span>
                <h2 className="font-serif text-2xl text-lux-ink tracking-tight">{s.heading}</h2>
                <p className="text-[15px] text-lux-sec leading-relaxed font-light whitespace-pre-line">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Container>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
